import React, { useState, useCallback, useEffect } from 'react';
import { Button, Box, CircularProgress } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import FeeDetailsTable from '../components/FeeDetailsTable';
import { StudentPaymentSchemeWithMetrics } from '../../../types/paymentScheme';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import { SnackbarSeverityType } from '../../../enums';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';

interface DeletePaymentSchemesDialogProps {
  open: boolean;
  onClose: () => void;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
  onUpdate: () => void;
  selectedPaymentSchemes: StudentPaymentSchemeWithMetrics[];
}

const DeletePaymentSchemesDialog: React.FC<DeletePaymentSchemesDialogProps> = ({
  open,
  onClose,
  onShowToast,
  onUpdate,
  selectedPaymentSchemes
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const resetState = useCallback(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const isConfirmDisabled = selectedPaymentSchemes.length === 0 || isLoading;

  const handleConfirm = async () => {
    setIsLoading(true);
    const selectedSchemeIds = selectedPaymentSchemes.map(
      (scheme: StudentPaymentSchemeWithMetrics) => scheme._id
    );
    try {
      await paymentSchemesApis.deletePaymentSchemes(selectedSchemeIds);
      onShowToast(
        'Payment schemes deleted successfully!',
        SnackbarSeverityType.success
      );
      onUpdate();
      onClose();
      resetState();
    } catch (error: any) {
      console.error('Error deleting payment schemes:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to delete payment schemes.',
        SnackbarSeverityType.error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const dialogActions = (
    <>
      <Button
        variant='contained'
        onClick={onClose}
        sx={{ textTransform: 'none' }}
        disabled={isLoading}
      >
        No
      </Button>
      <Button
        variant='contained'
        onClick={handleConfirm}
        sx={{ textTransform: 'none' }}
        disabled={isConfirmDisabled}
        startIcon={
          isLoading ? <CircularProgress size={20} color='inherit' /> : null
        }
      >
        Yes
      </Button>
    </>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Are you sure you want to delete the selected payment schemes ?'
      maxWidth='md'
      actions={dialogActions}
    >
      {isLoading && <OverlayLoader show={isLoading} />}
      <Box sx={{ mb: 2 }}>
        <FeeDetailsTable
          title={`${selectedPaymentSchemes.length} Results`}
          feeDetails={selectedPaymentSchemes}
          isLoading={false}
          checkboxSelection={false}
          onApproveRejectClick={() => {}}
          hideCustomColumns={true}
          onUpdateFeeStatusClick={() => {}}
        />
      </Box>
    </DialogModal>
  );
};

export default DeletePaymentSchemesDialog;
